import Modal from '@ui/modal/modal';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import { Dispatch, SetStateAction, useState } from 'react';
import { GateTypes, PasscodeList, Requirement } from '@/types/registration.types';
import PasscodeListCardRow from '../passcode-lists/passcode-list-row';
import SearchInput from '@ui/search-input/search-input';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getWorkingProjectSessions, selectWorkingProjectRegistrationSettings } from '@/store/slices/project/selectors';
import { NavLink } from 'react-router-dom';
import OptionalComponent from '@ui/optional-component/optional-component';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

type SelectListModalProps = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  passcodeLists: PasscodeList[];
  onSaveComplete?: () => void;
};

const SelectListModal = ({ open, setOpen, passcodeLists }: SelectListModalProps) => {
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const sessions = useAppSelector(getWorkingProjectSessions);

  const [searchValue, setSearchValue] = useState('');
  const [passcodeListsToAdd, setPasscodeListsToAdd] = useState<number[]>([]);

  const dispatch = useAppDispatch();

  const filteredLists = passcodeLists.filter((list) => {
    if (!searchValue) return true;
    const regex = new RegExp(`\\b${searchValue}`, 'i');
    return regex.test(list.name);
  });

  const handleClose = () => {
    setPasscodeListsToAdd([]);
    setOpen(false);
  };

  const handleSearch = (search: string) => {
    setSearchValue(search.toLowerCase());
  };

  const toggleSelectToAdd = (passcodeListId: number) => {
    setPasscodeListsToAdd((prevLists) => {
      if (prevLists.includes(passcodeListId)) {
        return prevLists.filter((id) => id !== passcodeListId);
      }
      return [...prevLists, passcodeListId];
    });
  };

  const handleSavePasscodeList = async () => {
    if (registrationSettings && passcodeListsToAdd.length) {
      const sessionIds = sessions?.map((session) => session.session) || [];
      const newWorkingGateRequirements: Requirement[] = [];

      passcodeListsToAdd.forEach((passcodeList) => {
        newWorkingGateRequirements.push({
          type: GateTypes.passcode_list,
          passcodeList: [passcodeList],
          gatedSessions: [...sessionIds],
        });
      });

      const requirementsCopy = { ...registrationSettings?.requirements };
      const listsCopy = [...(requirementsCopy?.[GateTypes.passcode_list] || [])];
      listsCopy.push(...newWorkingGateRequirements);

      const _registrationSettings = {
        ...registrationSettings,
        requirements: {
          ...registrationSettings.requirements,
          ...(listsCopy.length ? { [GateTypes.passcode_list]: listsCopy } : {}),
        },
      };

      dispatch(saveWorkingProjectByFields({ registration_settings: _registrationSettings }));
    }
    setPasscodeListsToAdd([]);
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onRequestClose={handleClose}
      customSize={{
        width: '600px',
        height: filteredLists.length > 0 ? '600px' : '400px',
        minHeight: '300px',
        maxHeight: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
      }}
      contentWrapperClassName="padding-24 overflow-y-auto flex-1 flex-column"
      backgroundColor="var(--gray-900)"
      renderHeaderContent={() => (
        <Container className="flex-column gap-8" borderRadius="16px">
          <Text className="H4">Add list</Text>
          <Text className="P1" color="var(--gray-500)">
            Select an audience list from your channel to apply to your project
          </Text>
        </Container>
      )}
      renderFooterContent={() => (
        <Container background="var(--gray-900)" className="flex fd-row jc-flex-end ai-flex-end gap-16">
          <Button
            onClick={handleClose}
            backgroundColor="var(--gray-600)"
            hoverBackgroundColor="var(--gray-500)"
            className="L1 padding-v-16 padding-h-24"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePasscodeList}
            className="padding-v-16 padding-h-24"
            disabledOpacity={0.6}
            backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
            disabled={filteredLists.length === 0}
          >
            <Text
              className="L1"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              Save
            </Text>
          </Button>
        </Container>
      )}
    >
      <Container className="flex-column gap-8 padding-v-24">
        <OptionalComponent display={filteredLists.length > 0}>
          <>
            <Container className="padding-b-8">
              <SearchInput onSubmit={handleSearch} debounceTime={300} onClear={() => handleSearch('')} />
            </Container>
            {filteredLists.map((passcodeList: PasscodeList) => {
              return (
                <PasscodeListCardRow
                  key={passcodeList.passcode_list}
                  passcodeList={passcodeList}
                  onClick={toggleSelectToAdd as Dispatch<SetStateAction<number>>}
                />
              );
            })}
          </>
        </OptionalComponent>
        <OptionalComponent display={filteredLists.length === 0}>
          <Container className="flex-column gap-16 ai-center jc-center padding-v-32">
            <Text className="P1" color="var(--gray-500)">
              No lists available. Create a list in your channel to get started.
            </Text>
            <NavLink to="/admin/people" onClick={handleClose}>
              <Button
                className="padding-v-16 padding-h-24"
                disabledOpacity={0.6}
                backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
              >
                <Text
                  className="L1"
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                >
                  Manage lists
                </Text>
              </Button>
            </NavLink>
          </Container>
        </OptionalComponent>
      </Container>
    </Modal>
  );
};

export default SelectListModal;
